import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { Feature, ReachProSupportClient } from 'src/WebApiController';

export const CONVERSATIONS_QUERY_KEY = 'reachpro-conversations';
export const CONVERSATIONS_REFRESH_QUERY_KEY = 'reachpro-conversations-refresh';
export const MESSAGES_QUERY_KEY = 'reachpro-messages';
export const LAST_REFRESH_KEY = 'reachpro-last-refresh-time';
export const REFRESH_INTERVAL_MS = 60000; // 1 minute

function isRefreshStale(): boolean {
  const lastRefresh = localStorage.getItem(LAST_REFRESH_KEY);
  if (!lastRefresh) return true;

  const timeSinceRefresh = Date.now() - parseInt(lastRefresh);
  return timeSinceRefresh > REFRESH_INTERVAL_MS;
}

export function useReachProConversations() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);

  const isOnReachProPage = pathname.startsWith('/reachpro-support');
  const shouldQuery =
    hasReachProSupportFeature &&
    activeAccountWebClientConfig.activeAccountId != null &&
    isOnReachProPage;

  const {
    data: conversations,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      CONVERSATIONS_QUERY_KEY,
      activeAccountWebClientConfig.activeAccountId,
    ],
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }
      return tryInvokeApi(
        async () => {
          return await new ReachProSupportClient(
            activeAccountWebClientConfig
          ).getConversations();
        },
        (error) => {
          trackError('ReachProSupportClient.getConversations', error);
        }
      );
    },
    enabled: shouldQuery,
    refetchInterval: shouldQuery ? 180000 : false, // 3 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const { data: refreshData } = useQuery({
    queryKey: [
      CONVERSATIONS_REFRESH_QUERY_KEY,
      activeAccountWebClientConfig.activeAccountId,
    ],
    queryFn: async () => {
      if (!shouldQuery) {
        return [];
      }
      const result = await tryInvokeApi(
        async () => {
          return await new ReachProSupportClient(
            activeAccountWebClientConfig
          ).refreshConversations();
        },
        (error) => {
          trackError('ReachProSupportClient.refreshConversations', error);
        }
      );

      if (result) {
        localStorage.setItem(LAST_REFRESH_KEY, Date.now().toString());
      }

      return result ?? [];
    },
    enabled: shouldQuery,
    refetchInterval: shouldQuery ? REFRESH_INTERVAL_MS : false,
    refetchOnMount: isRefreshStale,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (refreshData) {
      queryClient.setQueryData<typeof refreshData>(
        [CONVERSATIONS_QUERY_KEY, activeAccountWebClientConfig.activeAccountId],
        refreshData
      );
    }
  }, [refreshData, queryClient, activeAccountWebClientConfig.activeAccountId]);

  useEffect(() => {
    const activeConversationId = pathname.split('/').pop();
    if (
      activeConversationId &&
      activeConversationId !== 'reachpro-support' &&
      refreshData
    ) {
      queryClient.invalidateQueries({
        queryKey: [MESSAGES_QUERY_KEY, activeConversationId],
      });
    }
  }, [pathname, queryClient, refreshData]);

  return {
    conversations,
    isLoading,
    isError,
  };
}
